import React, { useMemo } from 'react'
import { css } from '@emotion/core'
import { Icon } from 'antd'
import { useTranslation } from 'react-i18next'
import type { RestaurantFilterOptions, RestaurantOverviewQuery } from '@/models/restaurant-manager'
import { ReloadButton } from '@/components/atoms/reload-button'
import { CalendarWithArrow } from '@/components/organisms/calendar-with-arrow'
import { FixedLabelMultiSelect } from '@/components/molecules/fixed-label-multi-select'
import { FixedLabelTimeRangeSelect } from '@/components/molecules/fixed-label-time-range-select'
import dayjs from 'dayjs'
import { RestaurantHighlightLegend } from './restaurant-highlight-legend'

type FilterFields = Pick<RestaurantOverviewQuery, 'subjectIds' | 'spaceIds' | 'nightIndexes' | 'startTimeFrom' | 'startTimeTo'>

const createClearedRestaurantOverviewFilters = (): FilterFields => ({
  subjectIds: [],
  spaceIds: [],
  nightIndexes: [],
  startTimeFrom: undefined,
  startTimeTo: undefined,
})

const countRestaurantOverviewSelectedFilterValues = ({
  subjectIds,
  spaceIds,
  nightIndexes,
  startTimeFrom,
  startTimeTo,
}: FilterFields): number => subjectIds.length + spaceIds.length + nightIndexes.length + (startTimeFrom ? 1 : 0) + (startTimeTo ? 1 : 0)

type Props = {
  date: string
  query: RestaurantOverviewQuery
  filterOptions?: RestaurantFilterOptions
  hasMealSpacePlugin: boolean
  onMoveDate: (date: dayjs.Dayjs) => void
  onReload: () => void
  onChangeQuery: (patch: Partial<RestaurantOverviewQuery>) => void
  pendingCancellationCount: number | null
  onOpenCancellationHistory: () => void
}

export const RestaurantFilterToolbar: React.FC<Props> = ({
  date,
  query,
  filterOptions,
  hasMealSpacePlugin,
  onMoveDate,
  onReload,
  onChangeQuery,
  pendingCancellationCount,
  onOpenCancellationHistory,
}) => {
  const { t } = useTranslation()
  const selectedFilterCount = countRestaurantOverviewSelectedFilterValues(query)
  const hasSelectedFilters = selectedFilterCount > 0

  const hasCancellation = useMemo(() => (pendingCancellationCount ?? 0) > 0, [pendingCancellationCount])

  return (
    <div css={toolbarWrapperStyle}>
      <div css={leftSectionStyle}>
        <div css={dateContainer}>
          <CalendarWithArrow dateOf={dayjs(date)} setDate={nextDate => onMoveDate(nextDate)} unit="day" displayFormat={t('YYYY-MM-DD')} />
          <ReloadButton onClick={onReload} css={{ marginLeft: '8px' }} />
        </div>

        <div css={filterContainerStyle}>
          <div css={groupTitleStyle}>{t('RestaurantManager.Filter')}</div>
          <div css={filterControlsStyle}>
            <FixedLabelMultiSelect
              label={t('RestaurantManager.SubjectNameSort')}
              value={query.subjectIds}
              options={(filterOptions?.subjects ?? []).map(subject => ({
                value: subject.subjectId,
                label: subject.subjectName,
              }))}
              disabled={!filterOptions}
              dropdownWidth={460}
              notFoundContent={t('No data available')}
              onChange={value => onChangeQuery({ subjectIds: value as string[] })}
            />
            <FixedLabelMultiSelect
              label={t('RestaurantManager.NightIndex')}
              value={query.nightIndexes}
              options={(filterOptions?.nightIndexes ?? []).map(nightIndex => ({
                value: nightIndex,
                label: t('RestaurantManager.NightIndexValue', { count: nightIndex }),
              }))}
              disabled={!filterOptions}
              width={100}
              onChange={value => onChangeQuery({ nightIndexes: value as number[] })}
            />
            {hasMealSpacePlugin && (
              <FixedLabelMultiSelect
                label={t('RestaurantManager.MealSpace')}
                value={query.spaceIds}
                options={(filterOptions?.spaces ?? []).map(space => ({
                  value: space.spaceId,
                  label: space.spaceName,
                }))}
                disabled={!filterOptions}
                dropdownWidth={460}
                onChange={value => onChangeQuery({ spaceIds: value as string[] })}
              />
            )}
            {hasMealSpacePlugin && (
              <FixedLabelTimeRangeSelect
                label={t('RestaurantManager.StartTime')}
                valueFrom={query.startTimeFrom}
                valueTo={query.startTimeTo}
                disabled={!filterOptions}
                minTime={filterOptions?.timeRange.min}
                maxTime={filterOptions?.timeRange.max}
                intervalMinutes={15}
                onChange={({ valueFrom, valueTo }) => onChangeQuery({ startTimeFrom: valueFrom, startTimeTo: valueTo })}
              />
            )}
            {hasSelectedFilters && (
              <button
                type="button"
                css={clearAllButtonStyle(hasSelectedFilters)}
                disabled={!hasSelectedFilters}
                onClick={() => onChangeQuery(createClearedRestaurantOverviewFilters())}
              >
                <span css={clearAllIconStyle}>×</span>
                <span>{t('RestaurantManager.ClearAllFilters')}</span>
                <span css={filterCountBadgeStyle}>{selectedFilterCount}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div css={rightSectionStyle}>
        <button type="button" css={cancellationButtonStyle(!hasCancellation)} onClick={onOpenCancellationHistory}>
          <Icon className="icon" type="bell" theme="filled" />
          <span className="text">{t('RestaurantManager.Cancellation')}</span>
          {hasCancellation ? <span css={cancellationBadgeStyle}>{pendingCancellationCount}</span> : null}
        </button>
      </div>
    </div>
  )
}

const toolbarWrapperStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  justifyContent: 'space-between',
  marginBottom: 16,
  padding: '0 12px',
})

const leftSectionStyle = css({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  flexWrap: 'wrap',
  gap: 24,
  minWidth: 0,
})

const rightSectionStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  marginLeft: 'auto',
})

const dateContainer = css({
  alignItems: 'center',
  display: 'flex',
  gap: 12,
})

const filterContainerStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  minWidth: 0,
})

const filterControlsStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
})

const groupTitleStyle = css({
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
})

const clearAllButtonStyle = (isEnabled: boolean) =>
  css({
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    color: isEnabled ? '#676767' : '#BFBFBF',
    cursor: isEnabled ? 'pointer' : 'default',
    display: 'inline-flex',
    fontSize: 14,
    gap: 6,
    padding: '0 4px',
  })

const clearAllIconStyle = css({
  color: '#676767',
  fontSize: 18,
  lineHeight: 1,
})

const filterCountBadgeStyle = css({
  alignItems: 'center',
  background: '#F8AA00',
  borderRadius: 999,
  color: '#FFFFFF',
  display: 'inline-flex',
  fontSize: 11,
  fontWeight: 'bold',
  height: 20,
  justifyContent: 'center',
  minWidth: 20,
  padding: '0 6px',
  letterSpacing: 0,
})

const cancellationButtonStyle = (disabled: boolean) =>
  css({
    alignItems: 'center',
    background: '#FFFFFF',
    border: disabled ? '1px solid #CCCCCC' : '1px solid #F8AA00',
    borderRadius: 18,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 14,
    fontWeight: 'bold',
    gap: 8,
    minHeight: 36,
    padding: '0 16px',
    position: 'relative',
    '.icon': {
      fontSize: 16,
      color: disabled ? '#CCCCCC' : '#F8AA00',
    },
    '.text': {
      color: disabled ? '#676767' : '#F8AA00',
    },
  })

const cancellationBadgeStyle = css({
  alignItems: 'center',
  background: '#F8AA00',
  borderRadius: 999,
  color: '#FFFFFF',
  display: 'inline-flex',
  fontSize: 11,
  fontWeight: 'bold',
  height: 20,
  justifyContent: 'center',
  minWidth: 20,
  padding: '0 6px',
  position: 'absolute',
  right: -8,
  top: -8,
  letterSpacing: 0,
})
